/**
 * Mock data for StreamGraph component.
 * Used for development and demo purposes when streams are not in the database.
 */

import { TaskStatus } from '../../task/TaskNode';

export interface MockNode {
  id: string;
  name: string;
  status: TaskStatus;
  x: number;
  y: number;
  meta?: string;
  agent?: { name: string };
}

export interface MockEdge {
  from: string;
  to: string;
  label?: string;
}

export interface MockGraphData {
  nodes: MockNode[];
  edges: MockEdge[];
}

export const STREAM_GRAPHS: Record<string, MockGraphData> = {
  'dns-opt': {
    nodes: [{
      id: '1',
      name: 'Define Requirements',
      status: 'completed',
      x: 50,
      y: 150
    }, {
      id: '2',
      name: 'Research DNS Patterns',
      status: 'completed',
      x: 300,
      y: 150
    }, {
      id: '3',
      name: 'Choose Strategy',
      status: 'attention',
      meta: 'Needs Decision',
      x: 550,
      y: 150
    }, {
      id: '4a',
      name: 'Implement Redis',
      status: 'running',
      agent: {
        name: 'Claude'
      },
      x: 800,
      y: 50
    }, {
      id: '5a',
      name: 'Load Test Redis',
      status: 'pending',
      x: 1050,
      y: 50
    }, {
      id: '4b',
      name: 'Implement Memcached',
      status: 'sleeping',
      meta: 'Paused',
      x: 800,
      y: 250
    }, {
      id: '6',
      name: 'Deploy to Staging',
      status: 'pending',
      x: 1300,
      y: 150
    }, {
      id: '7',
      name: 'Verify Metrics',
      status: 'pending',
      x: 1550,
      y: 150
    }],
    edges: [{
      from: '1',
      to: '2'
    }, {
      from: '2',
      to: '3'
    }, {
      from: '3',
      to: '4a',
      label: 'Redis'
    }, {
      from: '3',
      to: '4b',
      label: 'Memcached'
    }, {
      from: '4a',
      to: '5a'
    }, {
      from: '5a',
      to: '6'
    }, {
      from: '4b',
      to: '6'
    }, {
      from: '6',
      to: '7'
    }]
  },
  'q4-plan': {
    nodes: [{
      id: '1',
      name: 'Kickoff Meeting',
      status: 'completed',
      x: 50,
      y: 200
    }, {
      id: '2',
      name: 'Market Research',
      status: 'completed',
      x: 300,
      y: 100
    }, {
      id: '3',
      name: 'Competitor Analysis',
      status: 'completed',
      x: 300,
      y: 200
    }, {
      id: '4',
      name: 'User Interviews',
      status: 'running',
      agent: {
        name: 'GPT-4'
      },
      x: 300,
      y: 300
    }, {
      id: '5',
      name: 'Synthesize Findings',
      status: 'pending',
      x: 550,
      y: 200
    }, {
      id: '6',
      name: 'Define OKRs',
      status: 'pending',
      x: 800,
      y: 150
    }, {
      id: '7',
      name: 'Resource Planning',
      status: 'pending',
      x: 800,
      y: 250
    }, {
      id: '8',
      name: 'Budget Approval',
      status: 'pending',
      x: 1050,
      y: 150
    }, {
      id: '9',
      name: 'Roadmap Draft',
      status: 'pending',
      x: 1050,
      y: 250
    }, {
      id: '10',
      name: 'Stakeholder Review',
      status: 'pending',
      x: 1300,
      y: 200
    }, {
      id: '11',
      name: 'Finalize Q4 Plan',
      status: 'pending',
      x: 1550,
      y: 200
    }],
    edges: [{
      from: '1',
      to: '2'
    }, {
      from: '1',
      to: '3'
    }, {
      from: '1',
      to: '4'
    }, {
      from: '2',
      to: '5'
    }, {
      from: '3',
      to: '5'
    }, {
      from: '4',
      to: '5'
    }, {
      from: '5',
      to: '6'
    }, {
      from: '5',
      to: '7'
    }, {
      from: '6',
      to: '8'
    }, {
      from: '7',
      to: '9'
    }, {
      from: '8',
      to: '10'
    }, {
      from: '9',
      to: '10'
    }, {
      from: '10',
      to: '11'
    }]
  },
  'api-debug': {
    nodes: [{
      id: '1',
      name: 'Reproduce Issue',
      status: 'completed',
      x: 50,
      y: 150
    }, {
      id: '2',
      name: 'Check Logs',
      status: 'completed',
      x: 300,
      y: 150
    }, {
      id: '3',
      name: 'Profile Performance',
      status: 'completed',
      x: 550,
      y: 150
    }, {
      id: '4',
      name: 'Identify Bottleneck',
      status: 'completed',
      meta: '45m',
      x: 800,
      y: 150
    }, {
      id: '5',
      name: 'Fix Query N+1',
      status: 'running',
      agent: {
        name: 'Claude'
      },
      x: 1050,
      y: 150
    }, {
      id: '6',
      name: 'Run Tests',
      status: 'attention',
      meta: 'CI Failed',
      x: 1300,
      y: 150
    }],
    edges: [{
      from: '1',
      to: '2'
    }, {
      from: '2',
      to: '3'
    }, {
      from: '3',
      to: '4'
    }, {
      from: '4',
      to: '5'
    }, {
      from: '5',
      to: '6'
    }]
  },
  migration: {
    nodes: [{
      id: '1',
      name: 'Audit Legacy System',
      status: 'completed',
      x: 50,
      y: 150
    }, {
      id: '2',
      name: 'Document APIs',
      status: 'completed',
      x: 300,
      y: 150
    }, {
      id: '3',
      name: 'Design New Schema',
      status: 'completed',
      x: 550,
      y: 150
    }, {
      id: '4',
      name: 'Build Migration Scripts',
      status: 'completed',
      x: 800,
      y: 150
    }, {
      id: '5',
      name: 'Test Data Migration',
      status: 'completed',
      x: 1050,
      y: 150
    }, {
      id: '6',
      name: 'Migrate Users Table',
      status: 'completed',
      x: 1300,
      y: 100
    }, {
      id: '7',
      name: 'Migrate Orders Table',
      status: 'completed',
      x: 1300,
      y: 200
    }, {
      id: '8',
      name: 'Verify Data Integrity',
      status: 'completed',
      x: 1550,
      y: 150
    }, {
      id: '9',
      name: 'Update Application Code',
      status: 'sleeping',
      meta: 'Scheduled',
      x: 1800,
      y: 100
    }, {
      id: '10',
      name: 'Deploy to Staging',
      status: 'pending',
      x: 1800,
      y: 200
    }, {
      id: '11',
      name: 'Load Testing',
      status: 'pending',
      x: 2050,
      y: 150
    }, {
      id: '12',
      name: 'Production Cutover',
      status: 'pending',
      x: 2300,
      y: 150
    }],
    edges: [{
      from: '1',
      to: '2'
    }, {
      from: '2',
      to: '3'
    }, {
      from: '3',
      to: '4'
    }, {
      from: '4',
      to: '5'
    }, {
      from: '5',
      to: '6'
    }, {
      from: '5',
      to: '7'
    }, {
      from: '6',
      to: '8'
    }, {
      from: '7',
      to: '8'
    }, {
      from: '8',
      to: '9'
    }, {
      from: '8',
      to: '10'
    }, {
      from: '9',
      to: '11'
    }, {
      from: '10',
      to: '11'
    }, {
      from: '11',
      to: '12'
    }]
  },
  onboarding: {
    nodes: [{
      id: '1',
      name: 'User Research',
      status: 'completed',
      x: 50,
      y: 150
    }, {
      id: '2',
      name: 'Design Wireframes',
      status: 'running',
      agent: {
        name: 'GPT-4'
      },
      x: 300,
      y: 150
    }, {
      id: '3',
      name: 'Build Welcome Screen',
      status: 'pending',
      x: 550,
      y: 100
    }, {
      id: '4',
      name: 'Build Profile Setup',
      status: 'pending',
      x: 550,
      y: 200
    }, {
      id: '5',
      name: 'Integrate Analytics',
      status: 'pending',
      x: 800,
      y: 150
    }],
    edges: [{
      from: '1',
      to: '2'
    }, {
      from: '2',
      to: '3'
    }, {
      from: '2',
      to: '4'
    }, {
      from: '3',
      to: '5'
    }, {
      from: '4',
      to: '5'
    }]
  }
};

/**
 * Get mock graph data for a stream, returning empty if not found
 */
export function getMockGraphData(streamId: string): MockGraphData {
  return STREAM_GRAPHS[streamId] || { nodes: [], edges: [] };
}
